import deepmerge from 'deepmerge';
import { loggerInfo } from '@/modules/logger';
import { isAndroid, isMAC, isDarkTheme } from '@/modules/detector';
import { formatterAppShareHost } from '@/modules/url';

import { LogModule } from '@/data/config';
import { JSBRIDGE_HANDLER_NAME, JSBRIDGE_HANDLER_NAME_CALL } from '@/data/jsBridge';

import { getUrl } from '../url/common';
import { formatData, tryCatch } from './common';
import { getJsBridge } from './init';

import { JsBridge } from '@/interface';

/**
 * 获取配置信息
 * @param bridge
 * @param options data参数
 * @returns
 */
export const callHandlerConfig = (options = {}): Promise<JsBridge.IConfigData> => {
  return getJsBridge().then((bridge: JsBridge.IJsbridge) => {
    return tryCatch(resolve => {
      bridge.callHandler(
        JSBRIDGE_HANDLER_NAME_CALL.CONFIG,
        options,
        (configData: JsBridge.IFormatResType<JsBridge.IConfigData>) => {
          loggerInfo([`${JSBRIDGE_HANDLER_NAME_CALL.CONFIG} done`, configData], LogModule.JSBridge);
          const data: JsBridge.IConfigData = formatData(configData);
          resolve(data);
        }
      );
    });
  });
};

/**
 * 关闭操作
 * @param bridge
 * @returns
 */
export const callHandlerClose = (options = {}): Promise<void> => {
  return getJsBridge().then((bridge: JsBridge.IJsbridge) => {
    return tryCatch(resolve => {
      bridge.callHandler(JSBRIDGE_HANDLER_NAME_CALL.CLOSE, options, () => {
        loggerInfo(`${JSBRIDGE_HANDLER_NAME_CALL.CLOSE} done`, LogModule.JSBridge);
        resolve();
      });
    });
  });
};
/**
 * 自定义，退出，关闭回调
 * @param bridge
 * @returns
 */
export const callHandlerCloseWait = (): Promise<void> => {
  return getJsBridge().then((bridge: JsBridge.IJsbridge) => {
    return tryCatch(resolve => {
      bridge.callHandler(JSBRIDGE_HANDLER_NAME_CALL.CLOSE_WAIT, (response: { success: number }) => {
        loggerInfo([`${JSBRIDGE_HANDLER_NAME_CALL.CLOSE_WAIT} done`, response], LogModule.JSBridge);
        resolve();
      });
    });
  });
};

/**
 * 设置 右上角操作按钮组
 * 目前主要有 分享按钮 和 资讯收藏按钮
 */
export const callHandlerActionsButtons = (
  options: JsBridge.IActionButtons,
  isFormatterLink?: boolean
): Promise<{ success: boolean }> => {
  const callback = (bridge: JsBridge.IJsbridge, opts: JsBridge.IActionButtons) => {
    return tryCatch(resolve => {
      loggerInfo([`${JSBRIDGE_HANDLER_NAME_CALL.ACTION_BUTTONS} opts`, opts], LogModule.JSBridge);
      bridge.callHandler(
        JSBRIDGE_HANDLER_NAME_CALL.ACTION_BUTTONS,
        opts,
        (response: JsBridge.IFormatResType<{ success: boolean }>) => {
          loggerInfo(
            [`${JSBRIDGE_HANDLER_NAME_CALL.ACTION_BUTTONS} done`, response],
            LogModule.JSBridge
          );
          const data = formatData(response);
          resolve(data);
        }
      );
    });
  };

  // 判断是否传入需要转换的Link
  if (isFormatterLink) {
    const _options: any = deepmerge(
      {
        menu: {
          share: {
            link: '',
          },
        },
      },
      options
    );
    return Promise.all([
      getJsBridge(),
      formatterAppShareHost(_options?.menu.share.link || getUrl()),
    ]).then(([bridge, link]) => {
      _options.menu.share.link = link;
      return callback(bridge, _options);
    });
  }
  return getJsBridge().then(bridge => callback(bridge, options));
};

/**
 * H5 主动通知原生页面 执行某些操作
 * 跳转到原生页面，刷新某些数据，执行某项原生功能(如：埋点...)
 */
export const callHandlerToNative = (
  options: JsBridge.INativeOptions,
  isFormatterLink?: boolean
): Promise<void> => {
  const callback = (bridge: JsBridge.IJsbridge, opt: JsBridge.INativeOptions) => {
    return tryCatch(resolve => {
      loggerInfo([`${JSBRIDGE_HANDLER_NAME_CALL.TO_NATIVE} opt`, opt], LogModule.JSBridge);
      bridge.callHandler(
        JSBRIDGE_HANDLER_NAME_CALL.TO_NATIVE,
        opt,
        (response: JsBridge.IFormatResType<{ success: boolean }>) => {
          loggerInfo(
            [`${JSBRIDGE_HANDLER_NAME_CALL.TO_NATIVE} done`, response],
            LogModule.JSBridge
          );

          const data = formatData(response, { isDeep: true });
          resolve(data);
        }
      );
    });
  };

  // 判断是否传入需要转换的Link
  if (isFormatterLink && options.type === 'SHARE_PANEL') {
    const _options = deepmerge(
      {
        data: {
          link: '',
        },
      },
      options
    );
    return Promise.all([
      getJsBridge(),
      formatterAppShareHost(_options?.data.link || getUrl()),
    ]).then(([bridge, link]) => {
      _options.data.link = link;
      return callback(bridge, _options);
    });
  }
  return getJsBridge().then(bridge => callback(bridge, options));
};

/**
 * 搜索股票, 在转股的页面使用
 */
export const callHandlerStockSearch = (
  options: JsBridge.IStockSearchOptions
): Promise<JsBridge.IStockSearchData> => {
  return getJsBridge().then((bridge: JsBridge.IJsbridge) => {
    return tryCatch(resolve => {
      loggerInfo(
        [`${JSBRIDGE_HANDLER_NAME_CALL.STOCK_SEARCH} options`, options],
        LogModule.JSBridge
      );
      bridge.callHandler(
        JSBRIDGE_HANDLER_NAME_CALL.STOCK_SEARCH,
        options,
        (response: JsBridge.IFormatResType<JsBridge.IStockSearchData>) => {
          const data = formatData(response);
          resolve(data);
        }
      );
    });
  });
};

/**
 * 调用 深圳CA 扫描身份证
 */
export const callHandlerSZCAScanIdCard = (
  options: JsBridge.IIdCardInfoOptions
): Promise<JsBridge.ISZCAScanIDCard> => {
  return getJsBridge().then((bridge: JsBridge.IJsbridge) => {
    return tryCatch(resolve => {
      bridge.callHandler(
        JSBRIDGE_HANDLER_NAME_CALL.SZCA_SCAN_IDCARD,
        options,
        (response: JsBridge.IFormatResType<JsBridge.ISZCAScanIDCard>) => {
          const data = formatData(response);
          resolve(data);
        }
      );
    });
  });
};

/**
 * 调用 深圳CA 活体检测
 */
export const callHandlerSZCAScanLiveDetect = (
  options = {}
): Promise<JsBridge.ISZCAScanLiveDetect> => {
  return getJsBridge().then((bridge: JsBridge.IJsbridge) => {
    return tryCatch(resolve => {
      bridge.callHandler(
        JSBRIDGE_HANDLER_NAME_CALL.SZCA_SCAN_LIVEDETECT,
        options,
        (response: JsBridge.IFormatResType<JsBridge.ISZCAScanLiveDetect>) => {
          const data = formatData(response);
          resolve(data);
        }
      );
    });
  });
};

/**
 * ios调用获取SID
 */
export const callHandlerCheckSid = (
  options: JsBridge.ICheckSIDOptions
): Promise<JsBridge.ICheckSID> => {
  return getJsBridge().then((bridge: JsBridge.IJsbridge) => {
    return tryCatch((resolve, reject) => {
      loggerInfo([`${JSBRIDGE_HANDLER_NAME_CALL.CHECK_SID} options`, options], LogModule.JSBridge);
      bridge.callHandler(
        JSBRIDGE_HANDLER_NAME_CALL.CHECK_SID,
        options,
        (response: JsBridge.IFormatResType<{ response: JsBridge.ICheckSID }>) => {
          const data = formatData(response);
          if (data.success && data.data.sid) {
            resolve(data);
          } else {
            reject(data);
          }
        }
      );
    });
  });
};

/**
 * 美股行情聲明，MAC独有的关闭方法
 * @returns
 */
export const callHandlerTerminate = (): Promise<JsBridge.ICheckSID> => {
  return getJsBridge().then((bridge: JsBridge.IJsbridge) => {
    return tryCatch((resolve, reject) => {
      if (isMAC()) {
        bridge.callHandler(JSBRIDGE_HANDLER_NAME_CALL.TERMINATE, resolve);
      } else {
        reject(`Please use MAC to call this method ${JSBRIDGE_HANDLER_NAME_CALL.TERMINATE}`);
      }
    });
  });
};

/**
 * 标题修改调用
 * 兼容版本：v2.3.910+
 * @param {ITitleBarUserOptions} options
 * @returns {ITitleBarUser}
 */
export const callHandlerTitleBarUserInfo = (
  options: JsBridge.ITitleBarUserOptions
): Promise<any> => {
  return getJsBridge().then((bridge: JsBridge.IJsbridge) => {
    return tryCatch(resolve => {
      loggerInfo(
        [`${JSBRIDGE_HANDLER_NAME_CALL.TITLE_BAR_USERINFO} options`, options],
        LogModule.JSBridge
      );
      bridge.callHandler(
        JSBRIDGE_HANDLER_NAME_CALL.TITLE_BAR_USERINFO,
        options,
        (response: JsBridge.IFormatResType<JsBridge.ITitleBarUser>) => {
          const data = formatData(response);
          resolve(data);
        }
      );
    });
  });
};

/**
 * 苹果内购
 * @param {IApplyPayOptions} options
 * @returns {IApplyPay}
 */
export const callHandlerApplyPay = (
  options: JsBridge.IApplyPayOptions
): Promise<JsBridge.IApplyPay> => {
  return getJsBridge().then((bridge: JsBridge.IJsbridge) => {
    return tryCatch(resolve => {
      bridge.callHandler(
        JSBRIDGE_HANDLER_NAME_CALL.APPLY_PAY,
        options,
        (response: Record<string, JsBridge.IApplyPay>) => {
          const data = formatData(response);
          resolve(data);
        }
      );
    });
  });
};

/**
 * 调用 广东CA 人脸识别
 */
export const callHandlerGDCAScanLiveDetect = (options = {}) => {
  return getJsBridge().then(bridge => {
    return new Promise(resolve => {
      bridge.callHandler(JSBRIDGE_HANDLER_NAME.GDCA_SCAN_LIVEDETECT, options, (response: any) => {
        loggerInfo(
          [`${JSBRIDGE_HANDLER_NAME_CALL.GDCA_SCAN_LIVEDETECT} done`, response],
          LogModule.JSBridge
        );
        const data = formatData(response);
        if (isAndroid()) {
          const { data: _data } = data;
          resolve({
            ...data,
            data: JSON.parse(_data),
          });
          return;
        }
        resolve(data);
      });
    });
  });
};

/**
 * 该方法需要增加到每个路由跳转时调用一下。
 * 新WebView 配置包含：以下参数都是可选
 * 详情看桥的文档
 * https://wiki.hszq8.com//pages/viewpage.action?pageId=17990191
 * @param {object} header
 * @param {number} header[isCustom] // 是否需要自定义导航 1: 是 0: 否（默认1）
 * @param {number} header[isHide] 是否隐藏导航 1: 展示 0: 隐藏（默认1）
 * @param {number} header[isSlide] 默认根据系统 - 皮肤切换 1 黑色字体，背景白色（白色皮肤），0，白色字体，背景黑色（暗黑皮肤）默认根据当前系统皮肤
 * @param {string} header[backgroundColor] 背景色
 * @param {string} header[mainTitle] 主标题
 * @param {string} header[mainTitleColor] 主标题颜色
 * @param {string} header[subTitle] 副标题
 * @param {string} header[subTitleColor] 副标题颜色
 * @param {string} header[customBtn] 单个自定义按钮 跳转 仅支持toNative方式

 * @param {object} footer
 * @param {object} isHide // 是否需要沉浸式 0: 是; 1: 否
 */
export const callHandlerWebConfig = (options: JsBridge.IWebViewConfig): Promise<any> => {
  const _options = deepmerge(
    {
      header: {
        isCustom: 1,
        isHide: 0,
        isSlide: isDarkTheme() ? 0 : 1,
      },
    },
    options
  ) as JsBridge.IWebViewConfig;
  return getJsBridge().then((bridge: JsBridge.IJsbridge) => {
    return tryCatch(resolve => {
      bridge.callHandler(JSBRIDGE_HANDLER_NAME_CALL.WEBVIEW_CONFIG, _options, (response: any) => {
        const data = formatData(response);
        resolve(data);
      });
    });
  });
};

/**
 * 通用第三方桥配置
 * 支持版本：V2.5.010+
 * @param callback
 * @returns
 */
export const callHandlerThirdConfig = (options: JsBridge.IThirdConfig): Promise<any> => {
  return getJsBridge().then((bridge: JsBridge.IJsbridge) => {
    return tryCatch(resolve => {
      bridge.callHandler(JSBRIDGE_HANDLER_NAME_CALL.THIRD_CONFIG, options, (response: any) => {
        const data = formatData(response, { isDeep: true });
        resolve(data);
      });
    });
  });
};

/**
 * 用于H5调用原生设备方法，如拍照，相册等等
 * 支持版本：主站 V2.5.600+
 * @param {string} options[type] // (必填)Camera 唤起摄像机， OpenAlbum 打开相册， CameraAndAlbum 唤起相机+相册
 * @param {string} options[data] // (选填)与APP通信的参数（limitMaxHeight，limitMaxWidth，maxSize其中有个有值，才需要压缩 ）
 * @param {string} options[fetch] // (选填)请求接口参数
 * @returns
 */
export async function callHandlerOpeateConfig(options: {
  type: string;
  data: Record<string, any>;
  fetch: Record<string, any>;
}): Promise<{
  success: boolean;
  response: Record<string, any>;
  data: Record<string, any>;
}> {
  return getJsBridge().then((bridge: JsBridge.IJsbridge) => {
    return tryCatch(resolve => {
      bridge.callHandler(JSBRIDGE_HANDLER_NAME_CALL.OPERATR_CONFIG, options, (response: any) => {
        const data = formatData(response, { isDeep: true });
        return resolve(data);
      });
    });
  });
}

// --------------- 沙特展业 专属 ---------------------
/**
 * 沙特展业帮助中心H5客服弹窗
 * @returns
 */
export const callHandlerCustomService = (options = {}): Promise<any> => {
  return getJsBridge().then((bridge: JsBridge.IJsbridge) => {
    return tryCatch(resolve => {
      bridge.callHandler(JSBRIDGE_HANDLER_NAME_CALL.CUSTOMER_SERVICE, options, (response: any) => {
        const data = formatData(response);
        resolve(data);
      });
    });
  });
};

/**
 * 沙特展业图片预览
 * @returns
 */
export const callHandlerImagePreview = (options = {}): Promise<any> => {
  return getJsBridge().then((bridge: JsBridge.IJsbridge) => {
    return tryCatch(resolve => {
      bridge.callHandler(JSBRIDGE_HANDLER_NAME_CALL.IMAGE_PREVIEW, options, (response: any) => {
        const data = formatData(response);
        resolve(data);
      });
    });
  });
};

/**
 * 沙特展业行情订阅商品列表
 */
export const callHandlerQuoteProduct = (options: JsBridge.IQuoteProduct): Promise<any> => {
  return getJsBridge().then((bridge: JsBridge.IJsbridge) => {
    return tryCatch(resolve => {
      bridge.callHandler(JSBRIDGE_HANDLER_NAME_CALL.QUOTE_PRODUCT, options, (response: any) => {
        const data = formatData(response);
        resolve(data);
      });
    });
  });
};
